import Vue, { set } from 'vue'
import { Context, Inject } from '@nuxt/types/app'
import { TranslateResult } from 'vue-i18n/types'

export enum Type {
  danger = 'danger',
  info = 'info',
  warning = 'warning',
  success = 'success'
}

type Message = string | TranslateResult

interface INotificationOptions {
  buttonText?: string | TranslateResult,
  description?: string | TranslateResult,
  buttonFunction?: () => any,
  closeTimer?: boolean
}
interface INotification extends INotificationOptions{
  resetNotification: (id: string | number) => void,
  id: string | number
  type: Type,
  message: string | TranslateResult
}

declare module '@nuxt/types' {
  interface Context {
    $notifications: Notification
  }
}

class Notification {
  state: { notifications: [] }

  constructor() {
    this.state = Vue.observable({ notifications: [] })
  }

  getNotifications() {
    return this.state.notifications
  }

  setNotification(notification?: INotification | null) {
    // eslint-disable-next-line import/no-named-as-default-member
    if (notification?.closeTimer) {
      setTimeout(() => {
        notification.resetNotification(notification.id)
      }, 15000)
    }
    set(this.state, 'notifications', [...this.state.notifications, notification])
  }

  resetNotification() {
    this.setNotification(null)
  }

  addNotification(type: Type, message: Message, options?: INotificationOptions) {
    this.setNotification({
      resetNotification: (id: string | number) => {
        set(this.state, 'notifications', [...this.state.notifications.filter((notification: INotification) => notification.id !== id)])
      },
      id: new Date().valueOf() + Math.random(),
      closeTimer: true,
      type,
      message,
      ...options
    })
  }

  addSuccess(message: Message, options?: INotificationOptions) {
    this.addNotification(Type.success, message, options)
  }

  addDanger(message: Message, options?: INotificationOptions) {
    this.addNotification(Type.danger, message, options)
  }

  addWarning(message: Message, options?: INotificationOptions) {
    this.addNotification(Type.warning, message, options)
  }

  addInfo(message: Message, options?: INotificationOptions) {
    this.addNotification(Type.info, message, options)
  }
}

export default function (ctx: Context, inject: Inject) {
  const notifications = new Notification()

  if (process.server) {
    ctx.beforeNuxtRender(({ nuxtState }) => {
      nuxtState.notifications = notifications.state
    })
  }

  const { nuxtState = {} } = ctx || {}
  if (process.client && nuxtState.notifications) {
    notifications.state = Vue.observable(nuxtState.notifications)
  }

  inject('notifications', notifications)
}
